import React, { useEffect, useState, useMemo } from 'react';

interface ChartWidgetProps {
  pair: string;
}

const ChartWidget: React.FC<ChartWidgetProps> = ({ pair }) => {
  const [glitch, setGlitch] = useState(false);
  const [loadingNodes, setLoadingNodes] = useState(true);
  const [computeLoad, setComputeLoad] = useState(84.2);
  const [nodeSync, setNodeSync] = useState(99.98);
  const [probability, setProbability] = useState(78.5);

  // Simulated live data stream
  useEffect(() => {
    setLoadingNodes(true);
    const timer = setTimeout(() => setLoadingNodes(false), 1200);

    const dataInterval = setInterval(() => {
      setComputeLoad(prev => Math.min(99.9, Math.max(80.1, prev + (Math.random() - 0.5) * 2)));
      setNodeSync(prev => Math.min(100, Math.max(99.95, prev + (Math.random() - 0.5) * 0.01)));
      setProbability(prev => Math.min(99.4, Math.max(65.2, prev + (Math.random() - 0.5) * 5)));
    }, 1500);
    
    const glitchInterval = setInterval(() => {
      setGlitch(true);
      setTimeout(() => setGlitch(false), 120);
    }, 5500);

    return () => {
      clearTimeout(timer);
      clearInterval(dataInterval);
      clearInterval(glitchInterval);
    };
  }, [pair]);

  // Generate a simulated wave path for the "Neural Probability"
  const wavePath = useMemo(() => {
    let path = "M 0 50";
    for (let i = 0; i <= 100; i += 5) {
      const y = 50 + Math.sin(i * 0.2) * 20 + (Math.random() - 0.5) * 10;
      path += ` L ${i} ${y}`;
    }
    return path;
  }, [probability]);

  return (
    <div className="w-full h-full min-h-[700px] bg-black relative flex flex-col font-sans selection:bg-[#AEB877]/30 border border-white/5 rounded-[40px] overflow-hidden grid-bg">
      {/* Terminal Header */}
      <div className="flex items-center justify-between px-10 py-8 border-b border-white/5 bg-black/40 backdrop-blur-xl z-10 shrink-0">
        <div className="flex items-center gap-6">
          <div className="flex gap-1.5">
            <div className="w-2 h-2 rounded-full bg-white/10"></div>
            <div className="w-2 h-2 rounded-full bg-white/10"></div>
            <div className="w-2 h-2 rounded-full bg-[#AEB877] shadow-[0_0_10px_#AEB877]"></div>
          </div>
          <span className="text-[10px] font-black text-white/40 uppercase tracking-[0.4em] truncate">
            Neural_Interface // {pair}
          </span>
        </div>
        <div className="flex items-center gap-8 text-[10px] font-black text-white/20 uppercase tracking-widest">
          <span className="flex items-center gap-2">
            <span className="text-[#AEB877] w-1.5 h-1.5 rounded-full bg-[#AEB877] animate-pulse"></span> 
            <span className="hidden sm:inline">Live_Sync:</span> {nodeSync.toFixed(3)}%
          </span>
          <span className="hidden lg:block">Protocol: ECC-512</span>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col items-center justify-center p-8 lg:p-16 relative overflow-y-auto no-scrollbar">
        {loadingNodes ? (
          <div className="flex flex-col items-center space-y-10 relative z-20">
            <div className="relative w-28 h-28">
              <div className="absolute inset-0 border-4 border-white/5 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-[#AEB877] border-t-transparent rounded-full animate-spin shadow-[0_0_20px_#AEB877]"></div>
              <div className="absolute inset-6 border border-white/10 rounded-full animate-pulse"></div>
            </div>
            <div className="text-center">
              <p className="text-xs text-[#AEB877] uppercase tracking-[0.8em] animate-pulse font-black">Synchronizing_Weights</p>
              <p className="text-[10px] text-white/20 uppercase tracking-[0.2em] mt-3">Connecting to Alpha-Node cluster...</p>
            </div>
          </div>
        ) : (
          <div className={`w-full max-w-7xl transition-all duration-1000 space-y-16 ${glitch ? 'opacity-60 scale-[0.99]' : 'opacity-100 scale-100'}`}>
            
            {/* Robot Activity Title */}
            <div className="text-center space-y-6">
              <div className="inline-flex items-center gap-3 px-6 py-2 rounded-full border border-white/10 bg-white/5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#AEB877] shadow-[0_0_5px_#AEB877] animate-pulse" />
                <span className="text-[10px] font-black text-white/40 uppercase tracking-[0.4em]">Autonomous_Mode_Active</span>
              </div>
              <h3 className="text-3xl md:text-5xl lg:text-7xl font-black text-white uppercase tracking-tighter leading-none">
                Trading <span className="text-transparent bg-clip-text bg-gradient-to-b from-white to-white/20">{pair}</span>
              </h3>
            </div>

            {/* Central Display: Pair & Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
              
              {/* Left Stats */}
              <div className="space-y-6 order-2 lg:order-1">
                {[
                  { label: 'Compute Load', val: `${computeLoad.toFixed(2)}%`, sub: 'Neural Engine 4.0' },
                  { label: 'Latency Pulse', val: '4.2ms', sub: 'HFT Dedicated Link' },
                  { label: 'Win Probability', val: `${probability.toFixed(1)}%`, sub: 'Current Delta' }
                ].map((stat, i) => (
                  <div key={i} className="glass-card p-8 group">
                    <p className="text-[10px] font-black text-white/20 uppercase tracking-[0.3em] mb-2 group-hover:text-[#AEB877] transition-colors">{stat.label}</p>
                    <p className="text-3xl font-black text-white tracking-tighter tabular-nums">{stat.val}</p>
                    <p className="text-[9px] text-white/10 uppercase tracking-widest mt-2">{stat.sub}</p>
                  </div>
                ))}
              </div>

              {/* Center Hero: Pair */}
              <div className="text-center relative group order-1 lg:order-2">
                <div className="absolute -inset-24 bg-[#AEB877]/5 blur-[120px] rounded-full opacity-60 animate-pulse" />
                <h1 className="text-8xl md:text-9xl lg:text-[12rem] font-black tracking-tighter text-white uppercase relative z-10 select-none leading-none">
                  {pair.split('/')[0]}
                  <span className="text-[#AEB877] block text-3xl md:text-6xl mt-2 tracking-[0.3em] opacity-80 glow-text">
                    {pair.split('/')[1]}
                  </span>
                </h1>
              </div>

              {/* Right Analysis: Quantum Waveform */}
              <div className="space-y-6 order-3">
                <div className="glass-card p-8 relative overflow-hidden h-48">
                  <p className="text-[10px] font-black text-white/20 uppercase tracking-widest mb-6">Neural_Waveform</p>
                  <svg viewBox="0 0 100 100" className="w-full h-20 text-[#AEB877] overflow-visible">
                    <path
                      d={wavePath}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      className="animate-pulse"
                    />
                    <path
                      d={wavePath}
                      fill="url(#wave-grad)"
                      opacity="0.1"
                    />
                    <defs>
                      <linearGradient id="wave-grad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#AEB877" />
                        <stop offset="100%" stopColor="transparent" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="mt-6 flex justify-between items-end">
                    <div>
                      <p className="text-[8px] text-white/10 uppercase font-bold tracking-widest">Strategy Node</p>
                      <p className="text-xs font-black text-white uppercase tracking-tighter">QUANTUM_SCALPER_V4</p>
                    </div>
                    <div className="text-right">
                       <span className="text-[#AEB877] text-[10px] font-black uppercase tracking-widest animate-pulse">Entry_Ready</span>
                    </div>
                  </div>
                </div>

                <button className="w-full primary-button flex items-center justify-between group">
                  <div className="flex flex-col items-start">
                    <span className="text-[9px] font-black uppercase tracking-widest leading-none opacity-60">Market_Alpha</span>
                    <span className="text-xs font-bold mt-1">Aggregated Sentiment: 0.94</span>
                  </div>
                  <div className="w-10 h-10 bg-black/20 rounded-xl flex items-center justify-center group-hover:bg-black/40 transition-all">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m9 18 6-6-6-6"/></svg>
                  </div>
                </button>
              </div>

            </div>

            {/* Bottom Call to Action */}
            <div className="pt-12 flex flex-col items-center">
              <div className="w-32 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-8"></div>
              <div className="text-center space-y-4">
                <p className="text-[10px] text-white/20 font-black uppercase tracking-[0.4em] leading-relaxed">
                  Terminal Synchronized. Operational efficiency at <span className="text-white">98.4%</span>.
                </p>
                <button className="text-[11px] text-[#AEB877] font-black uppercase tracking-[0.3em] flex items-center justify-center gap-4 hover:text-white transition-colors">
                  <span className="w-2 h-2 bg-[#AEB877] rounded-full shadow-[0_0_10px_#AEB877] animate-pulse"></span>
                  View_Real_Time_Analytics
                </button>
              </div>
            </div>

          </div>
        )}
      </div>

      {/* Data Ticker Footer */}
      <div className="px-10 py-6 border-t border-white/5 bg-black/40 backdrop-blur-xl flex items-center justify-between z-10 shrink-0">
        <div className="flex gap-12 overflow-hidden whitespace-nowrap">
          <div className="flex gap-4">
            <span className="text-[9px] text-white/10 font-black uppercase tracking-widest">IO_LOAD:</span>
            <span className="text-[9px] text-[#AEB877] font-black tabular-nums">4.02 TiB/s</span>
          </div>
          <div className="hidden sm:flex gap-4">
            <span className="text-[9px] text-white/10 font-black uppercase tracking-widest">ERROR_RATE:</span>
            <span className="text-[9px] text-[#FF4D4D] font-black tabular-nums">0.000021%</span>
          </div>
          <div className="hidden md:flex gap-4">
            <span className="text-[9px] text-white/10 font-black uppercase tracking-widest">NETWORK:</span>
            <span className="text-[9px] text-white font-black">TCP_OPT_QUANTUM</span>
          </div>
        </div>
        <p className="text-[9px] font-black text-white/10 uppercase tracking-[0.5em] hidden sm:block">
          STATUS: EXECUTING_PROTOCOL_0x4F2A
        </p>
      </div>
    </div>
  );
};

export default ChartWidget;
